// Small iterative Radix-2 FFT (Cooley–Tukey).
// Public-domain implementation adapted for this project.

export function fft(realIn: Float64Array | Float32Array): { real: Float64Array; imag: Float64Array } {
  const n = realIn.length;
  if ((n & (n - 1)) !== 0) {
    throw new Error('FFT length must be a power of 2');
  }

  const real = new Float64Array(realIn);
  const imag = new Float64Array(n);

  // Bit-reversal permutation
  let j = 0;
  for (let i = 0; i < n; i++) {
    if (i < j) {
      const tReal = real[i];
      const tImag = imag[i];
      real[i] = real[j];
      imag[i] = imag[j];
      real[j] = tReal;
      imag[j] = tImag;
    }
    let m = n >> 1;
    while (m >= 1 && j >= m) {
      j -= m;
      m >>= 1;
    }
    j += m;
  }

  // Butterflies
  for (let s = 1; (1 << s) <= n; s++) {
    const m = 1 << s;
    const wmReal = Math.cos((-2 * Math.PI) / m);
    const wmImag = Math.sin((-2 * Math.PI) / m);

    for (let k = 0; k < n; k += m) {
      let wReal = 1;
      let wImag = 0;
      for (let l = 0; l < m / 2; l++) {
        const tReal = wReal * real[k + l + m / 2] - wImag * imag[k + l + m / 2];
        const tImag = wReal * imag[k + l + m / 2] + wImag * real[k + l + m / 2];
        const uReal = real[k + l];
        const uImag = imag[k + l];
        real[k + l] = uReal + tReal;
        imag[k + l] = uImag + tImag;
        real[k + l + m / 2] = uReal - tReal;
        imag[k + l + m / 2] = uImag - tImag;
        const nextWReal = wReal * wmReal - wImag * wmImag;
        wImag = wReal * wmImag + wImag * wmReal;
        wReal = nextWReal;
      }
    }
  }

  return { real, imag };
}
